import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
} from "@mui/material";
import Avatar from "@mui/material/Avatar";
import MuiDrawer from "@mui/material/Drawer";
import { Auth, Storage } from "aws-amplify";
import React, { useEffect, useState } from "react";
import { BiCog, BiEditAlt } from "react-icons/bi";
import { GoHome } from "react-icons/go";
import { TbLayoutKanban } from "react-icons/tb";
import { LuLayoutDashboard } from "react-icons/lu";
import { GoSearch } from "react-icons/go";
import { HiOutlineChatBubbleOvalLeft, HiOutlineMoon } from "react-icons/hi2";
import { IoMdArrowDropdown, IoIosRemoveCircleOutline } from "react-icons/io";
import {
  IoBookOutline,
  IoGitMergeOutline,
  IoLogOutOutline,
  IoNotificationsOutline,
  IoSettingsOutline,
} from "react-icons/io5";
import { LuFileChartLine, LuSunMedium } from "react-icons/lu";
import {
  MdKeyboardDoubleArrowLeft,
  MdKeyboardDoubleArrowRight,
  MdOutlineListAlt,
  MdOutlineRemoveRedEye,
} from "react-icons/md";
import { RiFileList3Line } from "react-icons/ri";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { fetchJSONFromS3, invokeLambdaWithRetry } from "services/Helper";
import config from "aws-exports";
import MeaIcon from "../../assets/images/mea_icon.png";
import MeaText from "../../assets/images/mea_text.svg";
import premium from "../../assets/images/premium.svg";
import { useAppThemeContext } from "../ContextProvider";
import { createUiLogger } from "../../../logging/uiLogger";
import { navigateFromPlatform } from "../../../appNavigation";
import { normalizeMeaOpsRoute } from "../../../meaOpsRoutes";
const console = createUiLogger("meaplatform/newui/components/Layout/index.jsx");



const iconMap = {
  GoHome,
  TbLayoutKanban,
  BiCog,
  BiEditAlt,
  LuLayoutDashboard,
  MdOutlineListAlt,
  LuFileChartLine,
  RiFileList3Line,
  MdOutlineRemoveRedEye,
  IoGitMergeOutline,
  IoBookOutline,
  HiOutlineChatBubbleOvalLeft,
};

// Adjusted styling for Triage icon (React Icon appear thinner)
const iconConfig = {
  TbLayoutKanban: { strokeWidth: 2.0  },
  LuLayoutDashboard: { strokeWidth: 2.0  },
};

const getIcon = (name) => {
  const IconComponent = iconMap[name];
  if (!IconComponent) return null;
  const config = iconConfig[name] || {};
  return <IconComponent {...config} />
};

const resolveBrandAsset = (assetPath) => {
  const normalizedPath = String(assetPath || "").trim();
  if (!normalizedPath) return "";
  if (/^https?:\/\//i.test(normalizedPath) || normalizedPath.startsWith("data:")) {
    return normalizedPath;
  }
  if (normalizedPath.startsWith("public/")) {
    return normalizedPath.replace(/^public\//, "");
  }
  if (normalizedPath.startsWith("system/")) {
    return normalizedPath;
  }
  return `system/config/meaOpsConfig/${normalizedPath.replace(/^\/+/, "")}`;
};

const resolveShowSwitchToClassic = (uiConfig) => {
  if (uiConfig?.accessControl?.showSwitchToClassic !== undefined) {
    return Boolean(uiConfig.accessControl.showSwitchToClassic);
  }
  return true;
};

const isMenuItemAllowedForRoles = (item, userGroups) => {
  if (!Array.isArray(item?.groups)) {
    return true;
  }

  return (Array.isArray(userGroups) ? userGroups : [])
    .some((group) => item.groups.includes(String(group || "").trim()));
};

const resolveRoleLabels = (groups) => {
  return [...new Set(
    (Array.isArray(groups) ? groups : [])
      .filter((group) => typeof group === "string" && group.startsWith("MEA_"))
      .map((group) =>
        group
          .replace(/^MEA_/, "")
          .toLowerCase()
          .replace(/(^|_)([a-z])/g, (_, prefix, character) =>
            `${prefix === "_" ? " " : ""}${character.toUpperCase()}`
          )
      )
  )];
};

const resolveRoleLabel = (groups) => {
  const roleLabels = resolveRoleLabels(groups);
  return roleLabels.length > 2
    ? `${roleLabels.slice(0, 2).join(", ")}...`
    : roleLabels.join(" & ");
};

const formatRoleLabels = (roleLabels) => {
  if (roleLabels.length <= 1) return roleLabels.join("");
  if (roleLabels.length === 2) return roleLabels.join(" & ");

  return `${roleLabels.slice(0, -1).join(", ")} & ${roleLabels[roleLabels.length - 1]}`;
};

const resolveMenuItemTitle = (item, userGroups) => {
  const title = item?.title || "";
  const groups = Array.isArray(userGroups) ? userGroups : [];
  const roleBasedTitle = item?.roleBasedTitle || {};
  const rules = Array.isArray(roleBasedTitle.rules) ? roleBasedTitle.rules : [];
  const matchesRule = (rule) => {
    const ruleGroups = Array.isArray(rule?.groups) ? rule.groups : [];
    if (ruleGroups.length === 0) return false;
    return rule.match === "any"
      ? ruleGroups.some((group) => groups.includes(group))
      : ruleGroups.every((group) => groups.includes(group));
  };
  const matchedTitleRule = rules.find(
    (rule) => rule.match !== "any" && matchesRule(rule)
  ) || rules.find(
    (rule) => rule.match === "any" && matchesRule(rule)
  );
  const configuredTitle = matchedTitleRule?.title || "";
  return String(configuredTitle || "").trim() || title;
};

const Layout = ({ toggleSwitchUI }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const {
    isDark,
    sidebarOpen,
    setSidebarOpen,
    handleClose,
    searchText,
    setSearchText,
    setGlobalSearchtext,
    setSearchMode,
    displayElement,
    setTemplateType,
    setLOBText,
    customerConfig,
    cognitoGroups
  } = useAppThemeContext();
  let [authType, setAuthType] = useState("mea");
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [currentTab, setCurrentTab] = React.useState("");
  const handleDrawer = () => setSidebarOpen((prev) => !prev);
  const [showSwitchToClassic, setShowSwitchToClassic] = useState(false);
  const [associatedUserGroups, setAssociatedUserGroups] = useState([]);
  let [username, setUsername] = useState("");
  let [userEmail, setUserEmail] = useState("");
  const [displayMenuItems, setDisplayMenuItems] = useState([]);
  const [cfg, setCfg] = useState({});
  const [profileInfo, setProfileInfo] = useState({ enabled: false, activeProfileName: "" });
  const [brandLogoUrl, setBrandLogoUrl] = useState("");
  const isQATool = location.pathname === "/user/qa-tool";

  useEffect(() => {
    if (cfg?.Ingestion?.items) {
      const homeItem = cfg.Ingestion.items.find(
        (item) => item.title === "Home"
      );
      const isHomeEnabled = !!(homeItem && homeItem.enabled);
      setCurrentTab(isHomeEnabled ? "Home" : "Submission summary");
    }
  }, [cfg]);

  useEffect(() => {
    setUser();
    fetchMenuItems();
    loadAppProfileState();
  }, []);

  useEffect(() => {
    fetchAssocUserGroups();
  }, [location]);

  const fetchMenuItems = async () => {
    const defaultFilePath = `system/config/uiConfiguration.json`;
    const defaultFileData = await fetchJSONFromS3(defaultFilePath);

    setShowSwitchToClassic(resolveShowSwitchToClassic(defaultFileData));

    if (!defaultFileData?.menuItems) {
      console.error("Menu config missing in S3 JSON");
      setDisplayMenuItems([]);
      return;
    }

    const cfg = defaultFileData.menuItems;
    setCfg(cfg);
  };

  useEffect(() => {
    if (!cfg || Object.keys(cfg).length === 0) {
      setDisplayMenuItems([]);
      return;
    }

    // sections (skip Premium)
    const sections = Object.entries(cfg)
      .filter(([key]) => key !== "Premium" && key !== "BusinessProcess")
      .map(([sectionTitle, sectionData]) => {
        const isVisible = sectionData?.visible !== false;
        const items = (sectionData?.items ?? []).filter(
          (i) => i?.visible !== false && isMenuItemAllowedForRoles(i, cognitoGroups)
        );

        return isVisible && items.length > 0 ? { title: sectionTitle, items } : null;
      })
      .filter(Boolean);

    // premium (flat item)
    if (cfg.Premium?.visible && isMenuItemAllowedForRoles(cfg.Premium, cognitoGroups)) {
      sections.push({
        title: "PremiumSection",
        items: [
          {
            ...cfg.Premium,
            isPremium: true, // just a flag to style differently
          },
        ],
      });
    }

    setDisplayMenuItems(sections);
  }, [cfg, cognitoGroups]);

  async function setUser() {
    try {
      const user = await Auth.currentAuthenticatedUser({
        bypassCache: true, // Optional, By default is false. If set to true, this call will send a request to Cognito to get the latest user data
      });
      if (user?.attributes?.email !== undefined) {
        setUserEmail(user?.attributes?.email);
        setUsername(
          user?.attributes?.given_name.charAt(0).toUpperCase() +
            user?.attributes?.given_name.slice(1) +
            " " +
            user?.attributes?.family_name.charAt(0).toUpperCase() +
            user?.attributes?.family_name.slice(1)
        );
      }
    } catch (err) {
      console.error(err);
    }
  }

  async function fetchAssocUserGroups() {
    try {
      let user = await Auth.currentAuthenticatedUser({
        bypassCache: true, // Optional, By default is false. If set to true, this call will send a request to Cognito to get the latest user data
      });
      if (!user?.attributes) {
        user = await Auth.currentAuthenticatedUser({
          bypassCache: true, // Optional, By default is false. If set to true, this call will send a request to Cognito to get the latest user data
        });
      }
      const body = {
        target: "activateUserAndAddToGroup",
        access_token: user.signInUserSession.idToken.jwtToken,
        option: "getGroupsList",
        userEmail: user?.attributes?.email,
        requiredFields: ['GroupName']
      };

      const init = {
        body: JSON.stringify(body),
      };
      const data = await invokeLambdaWithRetry("instanceHandlerLean", init);
      setAssociatedUserGroups(data?.associateUserGroups || []);
      return data?.associateUserGroups || [];
    } catch (err) {
      console.error("err: ", err);
      return false;
    }
  }

  async function loadAppProfileState() {
    try {
      const user = await Auth.currentAuthenticatedUser({
        bypassCache: true,
      });
      const data = await invokeLambdaWithRetry("instanceHandler", {
        body: JSON.stringify({
          access_token: user.signInUserSession.idToken.jwtToken,
          target: "get-app-profile-state",
        }),
      });
      setProfileInfo({
        enabled: data?.response?.enabled === true,
        activeProfileName: data?.response?.activeProfileName || "",
      });
    } catch (error) {
      console.error(error);
    }
  }

  const handleProfileClick = (event) => {
    if (anchorEl === null) {
      setAnchorEl(event.currentTarget);
    }
  };
  const handleProfileClose = () => {
    if (anchorEl !== null) {
      setAnchorEl(null);
    }
  };

  useEffect(() => {
    loadAuthType();
  }, []);

  var authTypePayload = undefined;
  async function loadAuthType() {
    try {
      if (authTypePayload === undefined) {
        const user = await Auth.currentAuthenticatedUser({
          bypassCache: true, // Optional, By default is false. If set to true, this call will send a request to Cognito to get the latest user data
        });

        var body = {
          access_token: user.signInUserSession.idToken.jwtToken,
          target: "getConfigValue",
          configName: "auth_type",
        };
        const init = {
          body: body,
        };
        const data = await invokeLambdaWithRetry("instanceHandler", init);
        authTypePayload = data;
      }

      setAuthType(authTypePayload.response);
      return authTypePayload;
    } catch (err) {
      console.error(err);
      return false;
    }
  }

  function reqFedLogout(userAttributes) {
    if (authType === "mea") return false;
    const index = userAttributes.findIndex((userAttribute) => {
      return (
        userAttribute.Name === "email" &&
        userAttribute.Value.toLowerCase().endsWith("@meaplatform.com")
      );
    });
    if (index !== -1) return false;
    return true;
  }

  const HeaderIcons = [
    {
      title: "Theme",
      icon: isDark ? (
        <HiOutlineMoon className="mea-icon" />
      ) : (
        <LuSunMedium className="mea-icon" />
      ),
      activity: () => {},
    },
    {
      title: "Notifications",
      icon: <IoNotificationsOutline className="mea-icon" />,
      activity: () => {},
    },
    {
      title: "Settings",
      icon: <IoSettingsOutline className="mea-icon" />,
      activity: () => {},
    },
    {
      title: "Logout",
      icon: <IoLogOutOutline className="mea-icon" />,
      activity: async function handleLogout() {
        try {
          var user = Auth.userPool.getCurrentUser();
          let userAuth = await Auth.currentAuthenticatedUser({
            bypassCache: true, // Optional, By default is false. If set to true, this call will send a request to Cognito to get the latest user data
          });
          var body = {
            access_token: userAuth.signInUserSession.idToken.jwtToken,
            target: "logout",
          };
          const init = {
            body: body,
          };
          await invokeLambdaWithRetry("instanceHandler", init, "Event");

          if (user) {
            if (
              user.storage &&
              user.storage[user.userDataKey] &&
              JSON.parse(user.storage[user.userDataKey]).UserAttributes &&
              reqFedLogout(
                JSON.parse(user.storage[user.userDataKey]).UserAttributes
              )
            ) {
              await Auth.signOut({ global: true });
            } else {
              await new Promise((resolve) => {
                user.getSession((err) => {
                  if (err) {
                    console.error("Error getting session:", err);
                    return resolve();
                  }
                  user.globalSignOut({
                    onSuccess: (result) => {
                      console.info("Global sign-out successful:", result);
                      resolve(result);
                    },
                    onFailure: (err) => {
                      console.error("Global sign-out failed:", err);
                      resolve();
                    },
                  });
                });
              });
            }
          }
          localStorage.setItem("logout", Date.now());
          window.location.href = "/login";
        } catch (error) {
          console.error("Logout failed", error);
        }
      },
    },
  ];

  const logoutIcon = HeaderIcons.find((item) => item.title === "Logout");
  const otherIcons = HeaderIcons.filter((item) => item.title !== "Logout");
  const branding = customerConfig?.branding || {};
  const hasBranding = branding?.enable === true && Boolean(branding?.logo?.source?.value);
  const brandLogoSource = resolveBrandAsset(branding?.logo?.source?.value);

  useEffect(() => {
    if (!brandLogoSource) {
      setBrandLogoUrl("");
      return;
    }

    (async () => {
      try {
        const isDirectUrl =
          /^https?:\/\//i.test(brandLogoSource) || brandLogoSource.startsWith("data:");
        const nextUrl = isDirectUrl
          ? brandLogoSource
          : await Storage.get(brandLogoSource, {
              level: "public",
              bucket: config.aws_user_files_s3_bucket,
              region: config.aws_project_region,
              download: false,
              cacheControl: "no-cache",
            });
        setBrandLogoUrl(nextUrl);
      } catch (error) {
        setBrandLogoUrl("");
      }
    })();
  }, [brandLogoSource]);

  useEffect(() => {
    if (displayMenuItems.length === 0) {
      return;
    }

    let foundTitle = null;
    const currentPath = location.pathname;
    for (const section of displayMenuItems) {
      const matchedTitles = section.items
        .filter((item) => item.route === currentPath)
        .map((item) => resolveMenuItemTitle(item, cognitoGroups));
      if (matchedTitles.length > 0) {
        foundTitle = matchedTitles.includes(currentTab)
          ? currentTab
          : matchedTitles[0];
        break;
      }
    }
    if (foundTitle) {
      setCurrentTab(foundTitle);
    } else {
      let path = "";
      if (location.pathname.startsWith("/user/")) {
        path = location.pathname.replace("/user/", "");
      } else if (location.pathname.startsWith("/home/")) {
        path = location.pathname.replace("/home/", "");
      } else {
        path = location.pathname.replace(/^\//, "");
      }
      let tabName = "Home";
      if (path) {
        const segments = path.split("/");
        if (segments.length > 1) {
          tabName = segments[0];
        } else {
          tabName = segments[0];
        }
      }
      const formattedTabName =
        tabName.charAt(0).toUpperCase() + tabName.slice(1).replace("-", " ");
      setCurrentTab(formattedTabName);
    }
  }, [location, displayMenuItems, cognitoGroups, currentTab]);

  return (
    <Box
      className={`layout-style ${isDark ? "dark" : ""}`}
      onClick={handleProfileClose}
      data-ui="new"
    >
      <Box className="layout-maincontainer">
        <Box
          className={`layout-aside ${sidebarOpen ? "open" : "closed"}`}
          open={sidebarOpen}
        >
          <Box
            className="flex-juscenter position-absolute fw-bold cursor-pointer p-1 text-light layout-menu-icon"
            onClick={() => setSidebarOpen(!sidebarOpen)}
          >
            {sidebarOpen ? (
              <MdKeyboardDoubleArrowLeft className="layout-hamburger" />
            ) : (
              <MdKeyboardDoubleArrowRight className="layout-hamburger" />
            )}
          </Box>
          <MuiDrawer
            variant="permanent"
            open={sidebarOpen}
            className={`layout-drawer`}
          >
            <Box
              className={`layout-custom-box ${
                sidebarOpen ? "text-center" : "text-center"
              }`}
              onClick={handleDrawer}
            >
              <img
                src={sidebarOpen ? MeaText : MeaIcon}
                alt="icon"
                onClick={() => navigateFromPlatform(navigate, "/user/home")}
                className={`cursor-pointer ${
                  sidebarOpen ? "layout-image-open" : "layout-image-closed"
                }`}
              />
            </Box>
            <Box className="layout-drawer-scrollbar">
              {displayMenuItems?.map((section, index) => (
                <div key={`sidebarfield${index}`}>
                  {section.title !== "PremiumSection" && (
                    <p
                      key={section.title}
                      className={`fw-500 text-title p-1 ${
                        sidebarOpen
                          ? "fs-rem07 sidebar-section-title"
                          : "fs-rem044 text-center"
                      }`}
                    >
                      {section.title}
                    </p>
                  )}

                  <List className="p-0 m-0">
                    <Box className="flex-col-justify-spacebetween">
                      {section?.items?.map((item) => {
                        const menuTitle = resolveMenuItemTitle(item, cognitoGroups);
                        const isCurrent = currentTab === menuTitle;
                        const isEnabled = item.enabled !== false;

                        return (
                          <ListItem
                            key={`${item.title}-${item.transactionType || ""}`}
                            onClick={() => {
                              if (!isEnabled || !item?.route) return;
                              const navigationRoute = normalizeMeaOpsRoute(item.route);
                              const navigationState = item?.transactionType
                                ? { transactionType: item.transactionType }
                                : undefined;
                              const proceeded = handleClose(navigationRoute, navigationState);
                              if (proceeded) {
                                setCurrentTab(menuTitle);
                                navigateFromPlatform(navigate, navigationRoute, {
                                  state: navigationState,
                                });
                                setSearchText("");
                                setTemplateType("");
                                setLOBText("");
                              }
                            }}
                            className={`flex-colcenter ${
                              item.isPremium
                                ? sidebarOpen
                                  ? "layout-premium-open-closed layout-premium-open"
                                  : "layout-premium-open-closed layout-premium-closed"
                                : "layout-subtitlecontainer"
                            } ${
                              isEnabled ? "cursor-pointer" : "cursor-notallowed"
                            }`}
                          >
                            {/* Premium vs normal styling */}
                            {item.isPremium ? (
                              <Box className="">
                                <img
                                  src={premium}
                                  alt="premium-icon"
                                  className="premium-iconsize"
                                />
                                {sidebarOpen && menuTitle}
                              </Box>
                            ) : (
                              <Box
                                className={`layout-current-tab-open-closed ${
                                  isCurrent
                                    ? sidebarOpen
                                      ? "fw-500 layout-opened-activetab"
                                      : "layout-closed-activetab"
                                    : "layout-opened-non-activetab"
                                }`}
                              >
                                <ListItemButton
                                  className={`layout-styled-list-item ${
                                    sidebarOpen ? "" : "justify-content-center"
                                  } ${
                                    isEnabled
                                      ? "cursor-pointer"
                                      : "cursor-notallowed"
                                  }`}
                                >
                                  <ListItemIcon
                                    className={`
                                    layout-styled-list-item-icon ${
                                      sidebarOpen ? "layout-open" : ""
                                    }
                                    ${
                                      isCurrent
                                        ? "layout-active"
                                        : "layout-inactive"
                                    }
                                    ${item.darkText ? "layout-text-dark" : ""}
                                  `}
                                  >
                                    {!sidebarOpen ? (
                                      <Tooltip
                                        title={menuTitle}
                                        placement="right"
                                        followCursor
                                        arrow
                                      >
                                        <span>
                                          {item.icon && getIcon(item.icon)}
                                        </span>
                                      </Tooltip>
                                    ) : ( 
                                      item.icon && getIcon(item.icon)
                                    )}
                                  </ListItemIcon>
                                  <ListItemText
                                    className={`layout-list-item-text ${
                                      sidebarOpen
                                        ? "layout-open"
                                        : "layout-closed"
                                    }
                                    ${item.darkText ? "layout-text-dark" : ""}
                                    ${
                                      isCurrent
                                        ? "layout-active"
                                        : "layout-inactive"
                                    }`}
                                  >
                                    <span>{menuTitle}</span>
                                  </ListItemText>
                                </ListItemButton>
                              </Box>
                            )}
                          </ListItem>
                        );
                      })}
                    </Box>
                  </List>
                </div>
              ))}
            </Box>
          </MuiDrawer>
        </Box>
        <Box className="layout-content">
          <Box className="layout-header">
            <Box className="layout-brand-search-group">
              {hasBranding ? (
                <Box className="layout-brand-container flex-center">
                  {brandLogoUrl ? (
                    <img
                      src={brandLogoUrl}
                      alt={branding?.logo?.altText || ""}
                      className="layout-brand-logo"
                    />
                  ) : null}
                </Box>
              ) : null}
              <Box
                className={`layout-search-container ${isQATool ? "search-disabled" : ""} ${hasBranding ? "with-branding" : ""}`}
              >
                <input
                  type="text"
                  placeholder="Search for templates, submissions or document"
                  className="layout-search-input font-Poppins"
                  value={searchText}
                  disabled={isQATool}
                  onChange={async (e) => {
                    setSearchText(e.target.value);
                  }}
                  onKeyDown={async (e) => {
                    if (e.key === "Enter" && searchText !== "") {
                      e.preventDefault();
                      setSearchMode("Filtered");
                      setGlobalSearchtext((prev) => !prev);
                    }
                  }}
                />
                <Tooltip title="Search" placement="bottom" arrow>
                  <span className="layout-searchbox">
                    {isQATool ? (
                      <IoIosRemoveCircleOutline className="search-stop-icon" />
                    ) : (
                      <GoSearch className="layout-search-icon cursor-pointer"
                        onClick={() => {
                          if (searchText !== "") {
                            setSearchMode("Filtered");
                            setGlobalSearchtext((prev) => !prev);
                          }
                        }}
                      />
                    )}
                  </span>
                </Tooltip>
              </Box>
            </Box>
            <Box className="flex-center mr-3 gap-7">
              <Box className="layout-header-dropdown flex-center cursor-pointer">
                <Box
                  className="layout-header-container gap-1 flex-alncenter justify-content-evenly"
                  onClick={handleProfileClick}
                >
                  <Avatar className="user-avatar">
                    {username.charAt(0).toUpperCase() +
                      username.charAt(username.indexOf(" ") + 1)}
                  </Avatar>

                  <Box className="flex flex-col">
                    <p className="body4">
                      <Tooltip title={userEmail} followCursor placement="top">
                        <span>{username}</span>
                      </Tooltip>
                    </p>
                    {associatedUserGroups.length > 0 && (
                      <p className="body5">
                        {associatedUserGroups[0]?.GroupName} | {resolveRoleLabel(cognitoGroups) && (
                          <Tooltip
                            title={formatRoleLabels(resolveRoleLabels(cognitoGroups))}
                            disableHoverListener={
                              resolveRoleLabel(cognitoGroups) ===
                              formatRoleLabels(resolveRoleLabels(cognitoGroups))
                            }
                            placement="top"
                            followCursor
                            arrow
                          >
                            <span>{resolveRoleLabel(cognitoGroups)}</span>
                          </Tooltip>
                        )}
                      </p>
                    )}
                  </Box>
                  {displayElement ? (
                    <IoMdArrowDropdown />
                  ) : (
                    <IoMdArrowDropdown color="transparent" />
                  )}
                </Box>
                {displayElement && anchorEl && (
                  <Box className="layout-styled-menu">
                    <Box className="layout-dropdownmenu-item">Profile</Box>
                    <Box className="layout-dropdownmenu-item">Groups</Box>
                  </Box>
                )}
              </Box>
              {showSwitchToClassic && (
                <Box className="flex-center gap-1">
                  <button
                    onClick={async () => {
                      const persisted = await toggleSwitchUI();
                      if (persisted) {
                        navigate("/user/submissions");
                        setSearchText("");
                      }
                    }}
                    className="modal-button business-button-modal"
                  >
                    <span className="btn btn-md btn-reduce">
                      Switch to Classic
                    </span>
                  </button>
                </Box>
              )}
              <Box className="flex-center gap-1">
                {displayElement &&
                  otherIcons.map((values) => (
                    <Box
                      key={values.title}
                      onClick={() => values?.activity()}
                      className="flex-center layout-notify-icons cursor-notallowed"
                    >
                      <Box className="layout-iconscontainer flex-center">
                        {values.icon}
                      </Box>
                    </Box>
                  ))}

                {/* Always show Logout */}
                {logoutIcon && (
                  <>
                    <Tooltip title="Logout" placement="top" arrow>
                      <Box
                        key={logoutIcon.title}
                        onClick={() => logoutIcon?.activity()}
                        className="flex-center layout-notify-icons cursor-pointer"
                      >
                        <Box className="layout-iconscontainer flex-center">
                          {logoutIcon.icon}
                        </Box>
                      </Box>
                    </Tooltip>
                  </>
                )}
              </Box>
            </Box>
            <Box className="layout-headerline"></Box>
          </Box>
          <Box className="layout-pagecontent">
            <Outlet />
          </Box>
          {profileInfo.enabled ? (
            <Box className="layout-footer">
              <span className="layout-footer-label">profile:</span>{" "}
              <strong>{profileInfo.activeProfileName || "Default"}</strong>
            </Box>
          ) : null}
        </Box>
      </Box>
    </Box>
  );
};

export default Layout;
